import { useEffect } from "react"
import { useNavigate } from "react-router-dom"
import { useDispatch } from "react-redux"
import { Box, Button, CircularProgress, Typography } from "@mui/material"
import { startOnboarding } from "../../../../redux/specialist-slices/wallet.slice"
import { showToast } from "../../../../redux/guest-slices/other.slice"
import { useState } from "react"

/**
 * ConnectRefresh
 *
 * Stripe redirects here when the onboarding link has expired or needs to be refreshed.
 * We automatically generate a new link and redirect the specialist.
 *
 * Route: /specialist/profile/connect/refresh
 */
function ConnectRefresh() {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const [retrying, setRetrying] = useState(false)
  const [failed, setFailed] = useState(false)

  const retryOnboarding = async () => {
    setRetrying(true)
    setFailed(false)
    const action = await dispatch(startOnboarding())
    if (startOnboarding.fulfilled.match(action)) {
      const { redirectUrl } = action.payload
      if (redirectUrl) {
        window.location.href = redirectUrl
        return
      }
    }
    setRetrying(false)
    setFailed(true)
    dispatch(showToast({ type: "error", message: "Failed to generate a new onboarding link. Please try again." }))
  }

  // Auto-retry on mount
  useEffect(() => {
    retryOnboarding()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <Box
      sx={{
        minHeight: "60vh",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 3,
      }}
    >
      <Box
        sx={{
          width: 80,
          height: 80,
          borderRadius: "50%",
          background: "linear-gradient(135deg,#fef3c7,#fde68a)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: "36px",
        }}
      >
        🔄
      </Box>

      <Typography variant="h6" fontWeight={700} color="other.color1">
        {failed ? "Link Expired" : "Refreshing Onboarding Link…"}
      </Typography>

      <Typography color="text.secondary" fontSize="14px" textAlign="center" maxWidth={360}>
        {failed
          ? "Your onboarding link has expired. Click below to generate a new one."
          : "Your previous onboarding link has expired. We're generating a fresh one — you'll be redirected shortly."}
      </Typography>

      {retrying && !failed && <CircularProgress size={28} thickness={4} color="warning" />}

      {failed && (
        <Box sx={{ display: "flex", gap: 1.5 }}>
          <Button
            variant="contained"
            onClick={retryOnboarding}
            disabled={retrying}
            sx={{
              textTransform: "none",
              borderRadius: "10px",
              fontWeight: 600,
              bgcolor: "#d97706",
              "&:hover": { bgcolor: "#b45309" },
            }}
          >
            {retrying ? "Generating…" : "Generate New Link"}
          </Button>
          <Button
            variant="outlined"
            onClick={() => navigate("/specialist/profile/wallet", { replace: true })}
            sx={{ textTransform: "none", borderRadius: "10px" }}
          >
            Back to Wallet
          </Button>
        </Box>
      )}
    </Box>
  )
}

export default ConnectRefresh
